import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useEntitlement } from "@/lib/sessionQueries";
import { useSetWorkspaceStatus, useDeleteClient, useDeleteWorkspace } from "@/lib/coachQueries";
import { useMeetingConfig, useSaveMeetingConfig, detailMessage } from "@/lib/zoomQueries";
import { useSetSharedPresentation } from "@/lib/teamWorkQueries";
import { ViewAsClientButton } from "../ViewAsClientButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Video, Presentation as PresentationIcon, AlertTriangle } from "lucide-react";
import { TypeToConfirmDialog } from "../../shared/TypeToConfirmDialog";

const TRANSITIONS = {
  draft: ["paused", "completed", "archived"], active: ["paused", "completed", "archived"],
  paused: ["draft", "completed", "archived"], completed: ["draft", "archived"], archived: [],
};
const STATUS_LABELS = { draft: "Resume to Active", paused: "Pause", completed: "Mark Completed", archived: "Archive" };

/**
 * Master v5 Prompt 17 — View as Client (exact WorkspaceMembership
 * selection, for a team Workspace) + workspace lifecycle transitions.
 */
export default function AccessSettingsTab({ workspaceId, memberId, overview }) {
  const navigate = useNavigate();
  const isTeam = overview.access_type === "team";
  const { data: entitlement } = useEntitlement(workspaceId);
  const teamMembers = isTeam ? (entitlement?.members || []) : [];
  const [targetMemberId, setTargetMemberId] = useState(memberId);
  const setStatus = useSetWorkspaceStatus(workspaceId);
  const setSharedPresentation = useSetSharedPresentation(workspaceId);
  const { data: meetingConfig } = useMeetingConfig(workspaceId);
  const saveMeetingConfig = useSaveMeetingConfig(workspaceId);
  const [meetingNumber, setMeetingNumber] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [hostZoomUserId, setHostZoomUserId] = useState(meetingConfig?.zoom_host_zoom_user_id || "");
  const [deleteClientMode, setDeleteClientMode] = useState("access_only");
  const [showDeleteClient, setShowDeleteClient] = useState(false);
  const [showDeleteWorkspace, setShowDeleteWorkspace] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteClient = useDeleteClient(workspaceId);
  const deleteWorkspace = useDeleteWorkspace();

  useEffect(() => {
    if (meetingConfig?.configured) {
      setMeetingNumber(meetingConfig.zoom_meeting_number || "");
      setHostZoomUserId(meetingConfig.zoom_host_zoom_user_id || "");
    }
  }, [meetingConfig]);

  async function handleSaveMeetingConfig() {
    try {
      await saveMeetingConfig.mutateAsync({
        zoom_meeting_number: meetingNumber, zoom_meeting_password: meetingPassword || null, zoom_host_zoom_user_id: hostZoomUserId,
      });
      toast.success("Zoom meeting settings saved");
      setMeetingPassword("");
    } catch (err) {
      toast.error(detailMessage(err.body, "Couldn't save the meeting settings"));
    }
  }

  async function handleToggleSharedPresentation(enabled) {
    try {
      await setSharedPresentation.mutateAsync(enabled);
      toast.success(enabled ? "Shared Presentation enabled for the team" : "Shared Presentation disabled");
    } catch (err) {
      toast.error(detailMessage(err.body, "Couldn't update Shared Presentation"));
    }
  }

  const targetName = targetMemberId === memberId ? overview.member_name : teamMembers.find((m) => m.membership_id === targetMemberId)?.email;
  const allowedNext = TRANSITIONS[overview.status] || [];

  async function handleTransition(newStatus) {
    try {
      await setStatus.mutateAsync({ new_status: newStatus });
      toast.success(`Workspace moved to ${newStatus}`);
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Couldn't change status" : err.message);
    }
  }

  async function handleDeleteClient() {
    setDeleteError(null);
    try {
      await deleteClient.mutateAsync({ memberId, mode: deleteClientMode, confirmName: overview.member_name });
      toast.success(deleteClientMode === "full" ? `${overview.member_name} and all their data were permanently deleted` : `${overview.member_name}'s access was revoked`);
      setShowDeleteClient(false);
      navigate("/rehearsal-room/coach/clients");
    } catch (err) {
      setDeleteError(err.body?.detail || "Couldn't delete this client");
    }
  }

  async function handleDeleteWorkspace() {
    setDeleteError(null);
    try {
      await deleteWorkspace.mutateAsync({ workspaceId, confirmTitle: overview.workspace_title });
      toast.success(`"${overview.workspace_title}" was permanently deleted`);
      setShowDeleteWorkspace(false);
      navigate("/rehearsal-room/coach/clients");
    } catch (err) {
      setDeleteError(err.body?.detail || "Couldn't delete this Engagement");
    }
  }

  return (
    <div data-testid="client-360-access-settings-tab" className="space-y-6 max-w-lg">
      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-2">View as Client</h2>
        <p className="text-xs text-ink-soft mb-3">
          Opens this exact member's own Workspace Home in read-only mode, with a persistent banner and a
          30-minute auto-expiry. Every mutation is rejected server-side regardless.
        </p>
        {isTeam && teamMembers.length > 1 && (
          <Select value={targetMemberId} onValueChange={setTargetMemberId}>
            <SelectTrigger className="mb-3" data-testid="client-360-view-as-member-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {teamMembers.map((m) => (
                <SelectItem key={m.membership_id} value={m.membership_id}>{m.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <ViewAsClientButton
          workspaceId={workspaceId} workspaceTitle={overview.workspace_title}
          memberId={targetMemberId} memberName={targetName}
          testId="client-360-access-settings-view-as-button"
        />
      </div>

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-2">Workspace lifecycle</h2>
        <p className="text-xs text-ink-soft mb-3">Current status: <span className="font-medium text-ink uppercase">{overview.status}</span></p>
        <div className="flex flex-wrap gap-2">
          {allowedNext.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="client-360-lifecycle-terminal">Archived is a terminal state.</p>
          ) : (
            allowedNext.map((s) => (
              <button
                key={s}
                onClick={() => handleTransition(s)}
                disabled={setStatus.isPending}
                className="text-sm font-medium border border-ink/15 rounded-full px-4 py-1.5 text-ink hover:bg-ink/5 transition-colors disabled:opacity-50"
                data-testid={`client-360-lifecycle-${s}`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))
          )}
        </div>
      </div>

      {isTeam && (
        <div className="border border-ink/10 rounded-lg p-4 bg-white/50" data-testid="client-360-shared-presentation-card">
          <h2 className="font-serif text-lg text-ink mb-2 flex items-center gap-2"><PresentationIcon size={16} /> Shared Presentation</h2>
          <p className="text-xs text-ink-soft mb-3">
            When on, this team sees an extra "Presentation" destination in their sidebar and a shared Presentation
            surfaces in Our Work — never any one member's private draft.
          </p>
          <div className="flex items-center gap-3">
            <Switch
              checked={!!overview.shared_presentation_enabled}
              onCheckedChange={handleToggleSharedPresentation}
              disabled={setSharedPresentation.isPending}
              data-testid="client-360-shared-presentation-toggle"
            />
            <span className="text-sm text-ink">{overview.shared_presentation_enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>
      )}

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50" data-testid="client-360-zoom-meeting-config">
        <h2 className="font-serif text-lg text-ink mb-2 flex items-center gap-2"><Video size={16} /> Zoom Meeting</h2>
        <p className="text-xs text-ink-soft mb-3">
          The meeting number/password embedded in this Workspace's Session Room. Only ever sent to an authorized,
          in-window Session participant when they join, and never logged — but it is included in their join
          material and external Zoom link, so anyone that participant shares those with can also join.
        </p>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Meeting number (Personal Meeting ID)</label>
            <Input value={meetingNumber} onChange={(e) => setMeetingNumber(e.target.value)} placeholder="123456789" data-testid="zoom-config-meeting-number-input" />
          </div>
          <div>
            <label className="text-xs text-ink-soft mb-1 block">
              Meeting password {meetingConfig?.has_password && <span className="text-ink/40">(already set — leave blank to keep it)</span>}
            </label>
            <Input type="password" value={meetingPassword} onChange={(e) => setMeetingPassword(e.target.value)} placeholder={meetingConfig?.has_password ? "••••••••" : "Optional"} data-testid="zoom-config-meeting-password-input" />
            <p className="text-[11px] text-ink/40 mt-1">Use the meeting passcode (letters+numbers) from your Zoom invite — not the numeric phone dial-in password.</p>
          </div>
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Host Zoom account (email)</label>
            <Input value={hostZoomUserId} onChange={(e) => setHostZoomUserId(e.target.value)} placeholder="coach@example.com" data-testid="zoom-config-host-user-id-input" />
          </div>
          <button
            onClick={handleSaveMeetingConfig}
            disabled={saveMeetingConfig.isPending || !meetingNumber || !hostZoomUserId}
            className="text-sm font-medium border border-ink/15 rounded-full px-4 py-1.5 text-ink hover:bg-ink/5 transition-colors disabled:opacity-50"
            data-testid="zoom-config-save-button"
          >
            Save
          </button>
        </div>
      </div>

      <div className="border border-red-200 rounded-lg p-4 bg-red-50/50" data-testid="client-360-danger-zone">
        <h2 className="font-serif text-lg text-destructive mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Danger zone</h2>
        <p className="text-xs text-ink-soft mb-3">Permanent actions. Both require typing the exact name to confirm.</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setDeleteClientMode("access_only"); setShowDeleteClient(true); }}
            className="text-sm font-medium border border-red-200 rounded-full px-4 py-1.5 text-destructive hover:bg-red-100 transition-colors"
            data-testid="client-360-delete-client-open"
          >
            Delete {overview.member_name}
          </button>
          <button
            onClick={() => setShowDeleteWorkspace(true)}
            className="text-sm font-medium border border-red-200 rounded-full px-4 py-1.5 text-destructive hover:bg-red-100 transition-colors"
            data-testid="client-360-delete-workspace-open"
          >
            Delete this Engagement
          </button>
        </div>
      </div>

      <TypeToConfirmDialog
        open={showDeleteClient} onOpenChange={(o) => { setShowDeleteClient(o); if (!o) setDeleteError(null); }}
        title={`Delete ${overview.member_name}?`}
        description="Choose what happens to their history, then confirm by typing their name."
        confirmValue={overview.member_name}
        onConfirm={handleDeleteClient} isPending={deleteClient.isPending} error={deleteError}
        testId="client-360-delete-client-dialog"
      >
        <RadioGroup value={deleteClientMode} onValueChange={setDeleteClientMode} className="space-y-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <RadioGroupItem value="access_only" className="mt-0.5" data-testid="client-360-delete-mode-access-only" />
            <span><span className="font-medium text-ink">Remove access only</span> — {overview.member_name} loses access, but every session, file, feedback and document stays for you.</span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <RadioGroupItem value="full" className="mt-0.5" data-testid="client-360-delete-mode-full" />
            <span><span className="font-medium text-ink">Delete everything</span> — every session, file, feedback and document tied to {overview.member_name} is permanently removed too.</span>
          </label>
        </RadioGroup>
      </TypeToConfirmDialog>

      <TypeToConfirmDialog
        open={showDeleteWorkspace} onOpenChange={(o) => { setShowDeleteWorkspace(o); if (!o) setDeleteError(null); }}
        title={`Delete "${overview.workspace_title}"?`}
        description="This removes the entire Engagement — every member, session, file, presentation, feedback, message and contract/invoice tied to it. This can't be undone."
        confirmValue={overview.workspace_title}
        onConfirm={handleDeleteWorkspace} isPending={deleteWorkspace.isPending} error={deleteError}
        testId="client-360-delete-workspace-dialog"
      />
    </div>
  );
}
